import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../components/Layout/MainLayout';
import adminService from '../services/adminService';
import { useAuth } from '../contexts/AuthContext';

const UsersManagementPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    fatherName: '',
    phoneNumber: '',
    email: '',
    username: '',
    employeeId: '',
    place: '',
    joiningDate: '',
    dateOfBirth: '',
    bloodGroup: '',
    emergencyNumber: '',
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    bankName: '',
    salaryBasic: '',
    salaryDa: '',
    salaryHra: '',
    salarySpecialAllowance: '',
    deductionPf: '',
    deductionTds: '',
    deductionEsi: '',
    deductionProfessionalTax: '',
    deductionOther: '',
    deductionOtherDescription: ''
  });
  const [editFiles, setEditFiles] = useState({
    resume: null,
    joiningLetter: null,
    bankDocument: null,
    additionalDocs: []
  });
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    role: '',
    search: ''
  });
  const [pagination, setPagination] = useState({});
  const canManageUsers = ['ADMIN', 'CHAIRMAN'].includes(user?.role);
  const editableDetailRoles = ['HR', 'HRO', 'ADMIN', 'CHAIRMAN'];

  useEffect(() => {
    if (canManageUsers) {
      fetchUsers();
    }
  }, [canManageUsers, filters]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const result = await adminService.getAllUsers(filters);
      
      if (result.success) {
        setUsers(result.data.users);
        setPagination(result.data.pagination);
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Fetch users error:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    try {
      const result = await adminService.updateUserStatus(userId, !currentStatus);
      
      if (result.success) {
        toast.success(result.message);
        fetchUsers(); // Refresh the list
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Update status error:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleViewDetails = async (userId) => {
    try {
      setLoadingDetails(true);
      setShowDetailsModal(true);
      setIsEditingDetails(false);
      
      const result = await adminService.getUserDetails(userId);
      
      if (result.success) {
        setSelectedUser(result.data);
      } else {
        toast.error(result.error);
        setShowDetailsModal(false);
      }
    } catch (error) {
      console.error('Get user details error:', error);
      toast.error('Failed to load user details');
      setShowDetailsModal(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedUser(null);
    setIsEditingDetails(false);
    setEditFiles({
      resume: null,
      joiningLetter: null,
      bankDocument: null,
      additionalDocs: []
    });
  };

  const initializeEditForm = (userData) => {
    setEditForm({
      name: userData?.name || '',
      fatherName: userData?.fatherName || '',
      phoneNumber: userData?.phoneNumber || '',
      email: userData?.email || '',
      username: userData?.username || '',
      employeeId: userData?.employeeId || '',
      place: userData?.place || '',
      joiningDate: userData?.joiningDate
        ? new Date(userData.joiningDate).toISOString().split('T')[0]
        : '',
      dateOfBirth: userData?.dateOfBirth
        ? new Date(userData.dateOfBirth).toISOString().split('T')[0]
        : '',
      bloodGroup: userData?.bloodGroup || '',
      emergencyNumber: userData?.emergencyNumber || '',
      accountHolderName: userData?.bankDetails?.accountHolderName || '',
      accountNumber: userData?.bankDetails?.accountNumber || '',
      ifscCode: userData?.bankDetails?.ifscCode || '',
      bankName: userData?.bankDetails?.bankName || '',
      salaryBasic: userData?.salary?.basic ?? '',
      salaryDa: userData?.salary?.da ?? '',
      salaryHra: userData?.salary?.hra ?? '',
      salarySpecialAllowance: userData?.salary?.specialAllowance ?? '',
      deductionPf: userData?.deductions?.pf ?? '',
      deductionTds: userData?.deductions?.tds ?? '',
      deductionEsi: userData?.deductions?.esi ?? '',
      deductionProfessionalTax: userData?.deductions?.professionalTax ?? '',
      deductionOther: userData?.deductions?.other ?? '',
      deductionOtherDescription: userData?.deductions?.otherDescription || ''
    });
  };

  const handleEditFieldChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const startEditingDetails = () => {
    if (!selectedUser) return;
    initializeEditForm(selectedUser);
    setEditFiles({
      resume: null,
      joiningLetter: null,
      bankDocument: null,
      additionalDocs: []
    });
    setIsEditingDetails(true);
  };

  const cancelEditingDetails = () => {
    setIsEditingDetails(false);
    setEditFiles({
      resume: null,
      joiningLetter: null,
      bankDocument: null,
      additionalDocs: []
    });
    if (selectedUser) {
      initializeEditForm(selectedUser);
    }
  };

  const handleEditFileChange = (e, fileType) => {
    const selectedFiles = Array.from(e.target.files || []);

    if (fileType === 'additionalDocs') {
      if (selectedFiles.length > 5) {
        toast.error('Maximum 5 additional documents allowed');
        return;
      }
      setEditFiles((prev) => ({
        ...prev,
        additionalDocs: selectedFiles
      }));
      return;
    }

    setEditFiles((prev) => ({
      ...prev,
      [fileType]: selectedFiles[0] || null
    }));
  };

  const handleSaveDetails = async () => {
    if (!selectedUser) return;

    try {
      setSavingDetails(true);

      const payload = {
        name: editForm.name?.trim(),
        fatherName: editForm.fatherName?.trim(),
        phoneNumber: editForm.phoneNumber?.trim(),
        email: editForm.email?.trim(),
        username: editForm.username?.trim(),
        employeeId: editForm.employeeId?.trim(),
        place: editForm.place?.trim(),
        joiningDate: editForm.joiningDate || undefined,
        dateOfBirth: editForm.dateOfBirth || undefined,
        bloodGroup: editForm.bloodGroup?.trim() || undefined,
        emergencyNumber: editForm.emergencyNumber?.trim() || undefined
      };

      const hasBankInput = [
        editForm.accountHolderName,
        editForm.accountNumber,
        editForm.ifscCode,
        editForm.bankName
      ].some((value) => value !== '' && value !== null && value !== undefined);

      if (hasBankInput) {
        payload.bankDetails = {
          accountHolderName: editForm.accountHolderName?.trim(),
          accountNumber: editForm.accountNumber?.trim(),
          ifscCode: editForm.ifscCode?.trim().toUpperCase(),
          bankName: editForm.bankName?.trim()
        };
      }

      const hasSalaryInput = [
        editForm.salaryBasic,
        editForm.salaryDa,
        editForm.salaryHra,
        editForm.salarySpecialAllowance
      ].some((value) => value !== '' && value !== null && value !== undefined);

      if (hasSalaryInput) {
        payload.salary = {
          basic: Number(editForm.salaryBasic) || 0,
          da: Number(editForm.salaryDa) || 0,
          hra: Number(editForm.salaryHra) || 0,
          specialAllowance: Number(editForm.salarySpecialAllowance) || 0
        };
      }

      const hasDeductionInput = [
        editForm.deductionPf,
        editForm.deductionTds,
        editForm.deductionEsi,
        editForm.deductionProfessionalTax,
        editForm.deductionOther,
        editForm.deductionOtherDescription
      ].some((value) => value !== '' && value !== null && value !== undefined);

      if (hasDeductionInput) {
        payload.deductions = {
          pf: Number(editForm.deductionPf) || 0,
          tds: Number(editForm.deductionTds) || 0,
          esi: Number(editForm.deductionEsi) || 0,
          professionalTax: Number(editForm.deductionProfessionalTax) || 0,
          other: Number(editForm.deductionOther) || 0,
          otherDescription: editForm.deductionOtherDescription?.trim() || ''
        };
      }

      const hasFileUpdates = Boolean(
        editFiles.resume
        || editFiles.joiningLetter
        || editFiles.bankDocument
        || (editFiles.additionalDocs && editFiles.additionalDocs.length > 0)
      );

      let requestPayload = payload;

      if (hasFileUpdates) {
        const formData = new FormData();

        Object.entries(payload).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            return;
          }

          if (typeof value === 'object' && !Array.isArray(value)) {
            Object.entries(value).forEach(([nestedKey, nestedValue]) => {
              if (nestedValue === undefined || nestedValue === null || nestedValue === '') {
                return;
              }
              formData.append(`${key}.${nestedKey}`, nestedValue);
            });
          } else {
            formData.append(key, value);
          }
        });

        if (editFiles.resume) {
          formData.append('resume', editFiles.resume);
        }
        if (editFiles.joiningLetter) {
          formData.append('joiningLetter', editFiles.joiningLetter);
        }
        if (editFiles.bankDocument) {
          formData.append('bankDocument', editFiles.bankDocument);
        }
        if (editFiles.additionalDocs && editFiles.additionalDocs.length > 0) {
          editFiles.additionalDocs.forEach((doc) => formData.append('additionalDocs', doc));
        }

        requestPayload = formData;
      }

      const result = await adminService.updateUserDetails(selectedUser._id, requestPayload);

      if (result.success) {
        setSelectedUser(result.data);
        setIsEditingDetails(false);
        toast.success(result.message || 'User details updated successfully');
        fetchUsers();
      } else {
        toast.error(result.error || 'Failed to update user details');
      }
    } catch (error) {
      console.error('Update user details error:', error);
      toast.error(error?.response?.data?.error || 'Failed to update user details');
    } finally {
      setSavingDetails(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleColor = (role) => {
    const colors = {
      'ADMIN': 'bg-red-100 text-red-800',
      'HR': 'bg-blue-100 text-blue-800',
      'HRO': 'bg-purple-100 text-purple-800',
      'CANDIDATE': 'bg-green-100 text-green-800',
      'USER': 'bg-gray-100 text-gray-800',
      'CHAIRMAN': 'bg-yellow-100 text-yellow-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const getJoiningLetterUrl = (userDetails) => {
    if (!userDetails) return '';

    if (userDetails.joiningLetterUrl) return userDetails.joiningLetterUrl;
    if (userDetails.joiningLetter?.url) return userDetails.joiningLetter.url;
    if (userDetails.hrLifecycle?.joiningLetterUrl) return userDetails.hrLifecycle.joiningLetterUrl;

    if (Array.isArray(userDetails.documents)) {
      const joiningDoc = userDetails.documents.find((doc) => {
        const name = (doc?.name || '').toLowerCase();
        return name.includes('joining') || name.includes('offer') || name.includes('appointment');
      });

      if (joiningDoc?.url) return joiningDoc.url;
    }

    return '';
  };

  const getUserDisplayName = (userData) => {
    const firstName = String(userData?.firstName || '').trim();
    const lastName = String(userData?.lastName || '').trim();
    const combinedName = `${firstName} ${lastName}`.trim();
    const emailPrefix = String(userData?.email || '').split('@')[0];

    return (
      userData?.name ||
      userData?.fullName ||
      combinedName ||
      userData?.username ||
      userData?.employeeId ||
      userData?.phoneNumber ||
      emailPrefix ||
      '-'
    );
  };

  const exportUsersToExcel = () => {
    if (!users.length) {
      toast.warning('No users to export');
      return;
    }

    const headers = [
      'Name',
      'Email',
      'Phone Number',
      'Employee ID',
      'Role',
      'Place',
      'Status',
      'Created'
    ];

    const rows = users.map((userData) => ([
      getUserDisplayName(userData),
      userData?.email || '',
      userData?.phoneNumber || '',
      userData?.employeeId || '',
      userData?.role || '',
      userData?.place || '',
      userData?.isActive ? 'Active' : 'Inactive',
      formatDate(userData?.createdAt)
    ]));

    const tableHeader = headers.map((header) => `<th>${String(header)}</th>`).join('');
    const tableRows = rows
      .map((row) => `<tr>${row.map((cell) => `<td>${String(cell ?? '')}</td>`).join('')}</tr>`)
      .join('');

    const html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office"
            xmlns:x="urn:schemas-microsoft-com:office:excel"
            xmlns="http://www.w3.org/TR/REC-html40">
        <head><meta charset="UTF-8" /></head>
        <body>
          <table>
            <thead><tr>${tableHeader}</tr></thead>
            <tbody>${tableRows}</tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const today = new Date().toISOString().split('T')[0];

    link.href = url;
    link.setAttribute('download', `users-management-${today}.xls`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Users exported to Excel');
  };

  // Check if user can manage users
  if (!canManageUsers) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-gray-600">Access Denied</h2>
            <p className="text-gray-500 mt-2">This page is only accessible to Admin and Chairman users.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Users Management</h1>
              <p className="text-gray-600">Manage all system users and their access</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={exportUsersToExcel}
                className="bg-emerald-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-emerald-700 transition-colors"
              >
                Export Excel
              </button>
              <button
                onClick={() => navigate('/admin/create-user')}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Create Basic User
              </button>
              
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                name="role"
                value={filters.role}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Roles</option>
                <option value="ADMIN">Admin</option>
                <option value="HR">HR Employee</option>
                <option value="HRO">HR Officer</option>
                <option value="CANDIDATE">Candidate</option>
                <option value="USER">User</option>
                <option value="CHAIRMAN">Chairman</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search by phone or email"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Items per page</label>
              <select
                name="limit"
                value={filters.limit}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Users List</h2>
            <p className="text-sm text-gray-600 mt-1">
              Showing {users.length} of {pagination.total || 0} users
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Place
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((userData) => (
                    <tr key={userData._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {getUserDisplayName(userData)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {userData.email || userData.phoneNumber || 'No contact'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {userData.employeeId || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(userData.role)}`}>
                          {userData.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {userData.place || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          userData.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {userData.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(userData.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleViewDetails(userData._id)}
                            className="px-3 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded text-xs font-medium transition-colors"
                          >
                            Full Details
                          </button>
                          {userData.role !== 'ADMIN' && userData._id !== user.userId && (
                            <button
                              onClick={() => handleStatusToggle(userData._id, userData.isActive)}
                              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                userData.isActive
                                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                              }`}
                            >
                              {userData.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {pagination.page} of {pagination.pages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Full Details Modal */}
        {showDetailsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-900">User Details</h3>
                <button
                  onClick={closeDetailsModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              <div className="p-6">
                {loadingDetails ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-600 mt-2">Loading user details...</p>
                  </div>
                ) : selectedUser ? (
                  (() => {
                    const joiningLetterUrl = getJoiningLetterUrl(selectedUser);
                    const canEditSelectedUser = editableDetailRoles.includes(selectedUser.role);

                    return (
                  <div className="space-y-6">
                    {/* Basic Information */}
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Basic Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-600">User ID</label>
                          <p className="text-sm text-gray-900 font-mono bg-gray-100 px-2 py-1 rounded">{selectedUser._id}</p>
                        </div>
                        {(selectedUser.name || (isEditingDetails && canEditSelectedUser)) && (
                          <div>
                            <label className="block text-sm font-medium text-gray-600">Full Name</label>
                            {isEditingDetails && canEditSelectedUser ? (
                              <input
                                type="text"
                                name="name"
                                value={editForm.name}
                                onChange={handleEditFieldChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              <p className="text-sm text-gray-900">{selectedUser.name}</p>
                            )}
                          </div>
                        )}
                        {(selectedUser.fatherName || (isEditingDetails && canEditSelectedUser)) && (
                          <div>
                            <label className="block text-sm font-medium text-gray-600">Father's Name</label>
                            {isEditingDetails && canEditSelectedUser ? (
                              <input
                                type="text"
                                name="fatherName"
                                value={editForm.fatherName}
                                onChange={handleEditFieldChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              <p className="text-sm text-gray-900">{selectedUser.fatherName}</p>
                            )}
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-gray-600">Phone Number</label>
                          {isEditingDetails && canEditSelectedUser ? (
                            <input
                              type="text"
                              name="phoneNumber"
                              value={editForm.phoneNumber}
                              onChange={handleEditFieldChange}
                              maxLength={10}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          ) : (
                            <p className="text-sm text-gray-900">{selectedUser.phoneNumber}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-600">Email</label>
                          {isEditingDetails && canEditSelectedUser ? (
                            <input
                              type="email"
                              name="email"
                              value={editForm.email}
                              onChange={handleEditFieldChange}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          ) : (
                            <p className="text-sm text-gray-900">{selectedUser.email || 'Not provided'}</p>
                          )}
                        </div>
                        {(selectedUser.username || (isEditingDetails && canEditSelectedUser)) && (
                          <div>
                            <label className="block text-sm font-medium text-gray-600">Username</label>
                            {isEditingDetails && canEditSelectedUser ? (
                              <input
                                type="text"
                                name="username"
                                value={editForm.username}
                                onChange={handleEditFieldChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              <p className="text-sm text-gray-900 font-mono bg-gray-100 px-2 py-1 rounded">{selectedUser.username}</p>
                            )}
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-gray-600">Password</label>
                          <p className="text-sm text-gray-500 italic">Password is encrypted and cannot be displayed for security reasons</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-600">Role</label>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(selectedUser.role)}`}>
                            {selectedUser.role}
                          </span>
                        </div>
                        {(selectedUser.employeeId || (isEditingDetails && canEditSelectedUser)) && (
                          <div>
                            <label className="block text-sm font-medium text-gray-600">Employee ID</label>
                            {isEditingDetails && canEditSelectedUser ? (
                              <input
                                type="text"
                                name="employeeId"
                                value={editForm.employeeId}
                                onChange={handleEditFieldChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              <p className="text-sm text-gray-900 font-mono bg-blue-100 px-2 py-1 rounded">{selectedUser.employeeId}</p>
                            )}
                          </div>
                        )}
                        {(selectedUser.place || (isEditingDetails && canEditSelectedUser)) && (
                          <div>
                            <label className="block text-sm font-medium text-gray-600">Place</label>
                            {isEditingDetails && canEditSelectedUser ? (
                              <input
                                type="text"
                                name="place"
                                value={editForm.place}
                                onChange={handleEditFieldChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              <p className="text-sm text-gray-900">{selectedUser.place}</p>
                            )}
                          </div>
                        )}
                        {(selectedUser.joiningDate || (isEditingDetails && canEditSelectedUser)) && (
                          <div>
                            <label className="block text-sm font-medium text-gray-600">Joining Date</label>
                            {isEditingDetails && canEditSelectedUser ? (
                              <input
                                type="date"
                                name="joiningDate"
                                value={editForm.joiningDate}
                                onChange={handleEditFieldChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              <p className="text-sm text-gray-900">{formatDate(selectedUser.joiningDate)}</p>
                            )}
                          </div>
                        )}
                        {(selectedUser.dateOfBirth || (isEditingDetails && canEditSelectedUser)) && (
                          <div>
                            <label className="block text-sm font-medium text-gray-600">Date of Birth</label>
                            {isEditingDetails && canEditSelectedUser ? (
                              <input
                                type="date"
                                name="dateOfBirth"
                                value={editForm.dateOfBirth}
                                onChange={handleEditFieldChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              <p className="text-sm text-gray-900">{formatDate(selectedUser.dateOfBirth)}</p>
                            )}
                          </div>
                        )}
                        {(selectedUser.bloodGroup || (isEditingDetails && canEditSelectedUser)) && (
                          <div>
                            <label className="block text-sm font-medium text-gray-600">Blood Group</label>
                            {isEditingDetails && canEditSelectedUser ? (
                              <select
                                name="bloodGroup"
                                value={editForm.bloodGroup}
                                onChange={handleEditFieldChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Select Blood Group</option>
                                {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => (
                                  <option key={bg} value={bg}>{bg}</option>
                                ))}
                              </select>
                            ) : (
                              <p className="text-sm text-gray-900">{selectedUser.bloodGroup}</p>
                            )}
                          </div>
                        )}
                        {(selectedUser.emergencyNumber || (isEditingDetails && canEditSelectedUser)) && (
                          <div>
                            <label className="block text-sm font-medium text-gray-600">Emergency Contact Number</label>
                            {isEditingDetails && canEditSelectedUser ? (
                              <input
                                type="tel"
                                name="emergencyNumber"
                                value={editForm.emergencyNumber}
                                onChange={handleEditFieldChange}
                                maxLength={10}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              <p className="text-sm text-gray-900">{selectedUser.emergencyNumber}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Account Status */}
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Account Status</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-600">Status</label>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            selectedUser.isActive 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {selectedUser.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-600">Profile Completed</label>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            selectedUser.profileCompleted 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {selectedUser.profileCompleted ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-600">Created At</label>
                          <p className="text-sm text-gray-900">{formatDateTime(selectedUser.createdAt)}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-600">Updated At</label>
                          <p className="text-sm text-gray-900">{formatDateTime(selectedUser.updatedAt)}</p>
                        </div>
                      </div>
                    </div>

                    {/* HR Lifecycle Information */}
                    {editableDetailRoles.includes(selectedUser.role) && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">HR Lifecycle</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-600">Joining Letter</label>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              joiningLetterUrl 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {joiningLetterUrl ? 'Uploaded' : 'Not Uploaded'}
                            </span>
                            {joiningLetterUrl && (
                              <div className="mt-2 flex flex-wrap items-center gap-3">
                                <a
                                  href={joiningLetterUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center rounded-md bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                                >
                                  View Joining Letter
                                </a>
                                <a
                                  href={joiningLetterUrl}
                                  download={`joining-letter-${selectedUser.employeeId || selectedUser._id}.pdf`}
                                  className="inline-flex items-center rounded-md bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                                >
                                  Download
                                </a>
                              </div>
                            )}
                          </div>
                          {selectedUser.joinedAt && (
                            <div>
                              <label className="block text-sm font-medium text-gray-600">Joined At</label>
                              <p className="text-sm text-gray-900">{formatDateTime(selectedUser.joinedAt)}</p>
                            </div>
                          )}
                          <div>
                            <label className="block text-sm font-medium text-gray-600">Resignation Status</label>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              selectedUser.isResigned 
                                ? 'bg-red-100 text-red-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {selectedUser.isResigned ? 'Resigned' : 'Active Employee'}
                            </span>
                          </div>
                          {selectedUser.resignedAt && (
                            <div>
                              <label className="block text-sm font-medium text-gray-600">Resigned At</label>
                              <p className="text-sm text-gray-900">{formatDateTime(selectedUser.resignedAt)}</p>
                            </div>
                          )}
                        </div>
                        {selectedUser.resignationReason && (
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-600">Resignation Reason</label>
                            <p className="text-sm text-gray-900 bg-red-50 p-3 rounded">{selectedUser.resignationReason}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Bank Details */}
                    {editableDetailRoles.includes(selectedUser.role) && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Bank Details</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-600">Account Holder Name</label>
                            {isEditingDetails && canEditSelectedUser ? (
                              <input
                                type="text"
                                name="accountHolderName"
                                value={editForm.accountHolderName}
                                onChange={handleEditFieldChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              <p className="text-sm text-gray-900">{selectedUser.bankDetails?.accountHolderName || 'Not provided'}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-600">Account Number</label>
                            {isEditingDetails && canEditSelectedUser ? (
                              <input
                                type="text"
                                name="accountNumber"
                                value={editForm.accountNumber}
                                onChange={handleEditFieldChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              <p className="text-sm text-gray-900 font-mono bg-gray-100 px-2 py-1 rounded">{selectedUser.bankDetails?.accountNumber || 'Not provided'}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-600">IFSC Code</label>
                            {isEditingDetails && canEditSelectedUser ? (
                              <input
                                type="text"
                                name="ifscCode"
                                value={editForm.ifscCode}
                                onChange={handleEditFieldChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              <p className="text-sm text-gray-900 font-mono bg-gray-100 px-2 py-1 rounded">{selectedUser.bankDetails?.ifscCode || 'Not provided'}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-600">Bank Name</label>
                            {isEditingDetails && canEditSelectedUser ? (
                              <input
                                type="text"
                                name="bankName"
                                value={editForm.bankName}
                                onChange={handleEditFieldChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              <p className="text-sm text-gray-900">{selectedUser.bankDetails?.bankName || 'Not provided'}</p>
                            )}
                          </div>
                        </div>
                        {selectedUser.bankDetails?.bankDocumentUrl && (
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-600">Bank Document</label>
                            <a
                              href={selectedUser.bankDetails.bankDocumentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm underline"
                            >
                              View Bank Document
                            </a>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Salary Details */}
                    {editableDetailRoles.includes(selectedUser.role) && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Salary Structure</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-600">Basic Salary</label>
                            {isEditingDetails && canEditSelectedUser ? (
                              <input
                                type="number"
                                min="0"
                                name="salaryBasic"
                                value={editForm.salaryBasic}
                                onChange={handleEditFieldChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              <p className="text-sm text-gray-900 font-semibold">₹{Number(selectedUser.salary?.basic || 0).toLocaleString()}</p>
                            )}
                          </div>
                          <div>
                            <div>
                              <label className="block text-sm font-medium text-gray-600">DA</label>
                              {isEditingDetails && canEditSelectedUser ? (
                                <input
                                  type="number"
                                  min="0"
                                  name="salaryDa"
                                  value={editForm.salaryDa}
                                  onChange={handleEditFieldChange}
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              ) : (
                                <p className="text-sm text-gray-900 font-semibold">₹{Number(selectedUser.salary?.da || 0).toLocaleString()}</p>
                              )}
                            </div>
                          </div>
                          <div>
                            <div>
                              <label className="block text-sm font-medium text-gray-600">HRA</label>
                              {isEditingDetails && canEditSelectedUser ? (
                                <input
                                  type="number"
                                  min="0"
                                  name="salaryHra"
                                  value={editForm.salaryHra}
                                  onChange={handleEditFieldChange}
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              ) : (
                                <p className="text-sm text-gray-900 font-semibold">₹{Number(selectedUser.salary?.hra || 0).toLocaleString()}</p>
                              )}
                            </div>
                          </div>
                          <div>
                            <div>
                              <label className="block text-sm font-medium text-gray-600">Special Allowance</label>
                              {isEditingDetails && canEditSelectedUser ? (
                                <input
                                  type="number"
                                  min="0"
                                  name="salarySpecialAllowance"
                                  value={editForm.salarySpecialAllowance}
                                  onChange={handleEditFieldChange}
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              ) : (
                                <p className="text-sm text-gray-900 font-semibold">₹{Number(selectedUser.salary?.specialAllowance || 0).toLocaleString()}</p>
                              )}
                            </div>
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-600">Gross Salary</label>
                            <p className="text-lg text-gray-900 font-bold text-green-600">₹{Number(selectedUser.salary?.gross || 0).toLocaleString()}</p>
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-600">Total Salary</label>
                            <p className="text-lg text-gray-900 font-bold text-blue-600">₹{Number(selectedUser.salary?.total || 0).toLocaleString()}</p>
                          </div>
                        </div>

                        {isEditingDetails && canEditSelectedUser && (
                          <div className="mt-4">
                            <h5 className="text-md font-semibold text-gray-900 mb-3">Deductions</h5>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-600">PF</label>
                                <input
                                  type="number"
                                  min="0"
                                  name="deductionPf"
                                  value={editForm.deductionPf}
                                  onChange={handleEditFieldChange}
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-600">TDS</label>
                                <input
                                  type="number"
                                  min="0"
                                  name="deductionTds"
                                  value={editForm.deductionTds}
                                  onChange={handleEditFieldChange}
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-600">ESI</label>
                                <input
                                  type="number"
                                  min="0"
                                  name="deductionEsi"
                                  value={editForm.deductionEsi}
                                  onChange={handleEditFieldChange}
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-600">Professional Tax</label>
                                <input
                                  type="number"
                                  min="0"
                                  name="deductionProfessionalTax"
                                  value={editForm.deductionProfessionalTax}
                                  onChange={handleEditFieldChange}
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-600">Other Deduction</label>
                                <input
                                  type="number"
                                  min="0"
                                  name="deductionOther"
                                  value={editForm.deductionOther}
                                  onChange={handleEditFieldChange}
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-600">Other Deduction Description</label>
                                <input
                                  type="text"
                                  name="deductionOtherDescription"
                                  value={editForm.deductionOtherDescription}
                                  onChange={handleEditFieldChange}
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {!isEditingDetails && (
                          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-600">PF</label>
                              <p className="text-sm text-gray-900">₹{Number(selectedUser.deductions?.pf || 0).toLocaleString()}</p>
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-600">TDS</label>
                              <p className="text-sm text-gray-900">₹{Number(selectedUser.deductions?.tds || 0).toLocaleString()}</p>
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-600">ESI</label>
                              <p className="text-sm text-gray-900">₹{Number(selectedUser.deductions?.esi || 0).toLocaleString()}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Documents */}
                    {editableDetailRoles.includes(selectedUser.role) && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Documents</h4>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-600">Resume</label>
                            {selectedUser.resumeUrl ? (
                              <a
                                href={selectedUser.resumeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 text-sm underline"
                              >
                                View Resume
                              </a>
                            ) : (
                              <p className="text-sm text-gray-500">Not uploaded</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-600">Joining Letter</label>
                            {joiningLetterUrl ? (
                              <a
                                href={joiningLetterUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 text-sm underline"
                              >
                                View Joining Letter
                              </a>
                            ) : (
                              <p className="text-sm text-gray-500">Not uploaded</p>
                            )}
                          </div>

                          {selectedUser.documents && selectedUser.documents.length > 0 ? (
                            <div>
                              <label className="block text-sm font-medium text-gray-600">Additional Documents</label>
                              <div className="space-y-2">
                                {selectedUser.documents.map((doc, index) => (
                                  <div key={index} className="flex items-center gap-2">
                                    <a 
                                      href={doc.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-800 text-sm underline"
                                    >
                                      {doc.name}
                                    </a>
                                    <span className="text-xs text-gray-500">
                                      (Uploaded: {formatDate(doc.uploadedAt)})
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">No additional documents uploaded</p>
                          )}

                          {isEditingDetails && canEditSelectedUser && (
                            <div className="mt-4 rounded-lg border border-gray-200 p-4 bg-gray-50">
                              <h5 className="text-sm font-semibold text-gray-900 mb-3">Re-upload / Upload Documents</h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-600 mb-1">Resume</label>
                                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleEditFileChange(e, 'resume')} className="w-full text-sm" />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-600 mb-1">Joining Letter</label>
                                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleEditFileChange(e, 'joiningLetter')} className="w-full text-sm" />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-600 mb-1">Bank Document</label>
                                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleEditFileChange(e, 'bankDocument')} className="w-full text-sm" />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-600 mb-1">Additional Documents (Max 5)</label>
                                  <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleEditFileChange(e, 'additionalDocs')} className="w-full text-sm" />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                    );
                  })()
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-600">Failed to load user details</p>
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex flex-wrap gap-3 justify-end">
                {selectedUser && editableDetailRoles.includes(selectedUser.role) && !isEditingDetails && (
                  <button
                    onClick={startEditingDetails}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                  >
                    Edit Details
                  </button>
                )}

                {isEditingDetails && (
                  <>
                    <button
                      onClick={cancelEditingDetails}
                      disabled={savingDetails}
                      className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors disabled:opacity-60"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveDetails}
                      disabled={savingDetails}
                      className="bg-green-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-60"
                    >
                      {savingDetails ? 'Saving...' : 'Save Changes'}
                    </button>
                  </>
                )}

                <button
                  onClick={closeDetailsModal}
                  className="bg-gray-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default UsersManagementPage;
